import { Button, Card, Input, message, Table, TableProps, Tooltip } from 'antd';
import React, { useEffect } from 'react'
import { FileAddOutlined, SyncOutlined } from '@ant-design/icons';
import { useUserContext } from '../../context/UserContext';
import { createPricingDeal, getHubspotDeals, refreshDeals } from '../../services/Apis';
import { useNavigate } from 'react-router-dom';
import { generateTableColumnFilters } from '../services/services';
import { currencySymbolsMap } from '../../context/CurrencyContext';

const { Search } = Input;

interface DataType {
    key: string,
    dealId: string,
    client: string,
    dealName: string,
    startDate: string,
    Stage: string,
    dealAmount: string,
    dealOwner: string,
    HubSpotLink: string,
    canCreatePricing: boolean,
    duration: number,
    currency: string
}

interface DealData {
    deal_id: number;
    deal_name: string;
    deal_owner_name: string;
    company_name: string;
    project_start_date: string;
    hubspot_link: string;
    deal_amount: number;
    deal_stage_name: string;
    can_create_pricing: boolean;
    is_owner: boolean;
    duration_in_months: number;
    currency: string;
}

interface NoPricingDealsTableProps {
  displayUserSpecificDeals: boolean;
  hideOpen: boolean,
  hideClosed: boolean;
}

const NoPricingDealsTable: React.FC<NoPricingDealsTableProps> =({displayUserSpecificDeals = false, hideOpen=false, hideClosed=false}) => {
    const [searchTerm, setSearchTerm] = React.useState<string>("");
    const [deals, setDeals] = React.useState<DataType[]>([]);
    const [filteredDeals, setFilteredDeals] = React.useState(deals);
    const [loading, setLoading] = React.useState<boolean>(true);
    const { userContext, setUserContext } = useUserContext();
    const [tokenExpired, setTokenExpired] = React.useState<boolean>(false);
    const navigate = useNavigate();
    const [refreshingDeal, setRefreshingDeal] = React.useState<string>('');
    const [disableCreateButton, setDisableCreateButton] = React.useState<boolean>(false);

    const deepSearch = (data: DataType[], searchTerm: string): DataType[] => {
        return data.filter((item) =>
          Object.values(item).some(
            (value) =>
              (typeof value === "string" &&
                value.toLowerCase().includes(searchTerm.toLowerCase())) ||
              (Array.isArray(value) &&
                value.some(
                  (element) =>
                    typeof element === "string" &&
                    element.toLowerCase().includes(searchTerm.toLowerCase())
                )) ||
              (typeof value === "object" &&
                value !== null &&
                deepSearch([value], searchTerm).length > 0)
          )
        );
      };
    
      const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        const term = event.target.value;
        setSearchTerm(term);
        if (term) {
          const results = deepSearch(deals, term);
          setFilteredDeals(results);
        } else {
          setFilteredDeals(deals);
        }
      };

    const formatCurrency = (amount: number, currency: string): string => {
        try {
            return `${currencySymbolsMap[currency] || '$'} ${Math.round(amount)?.toLocaleString('en-US', { minimumFractionDigits: 0 })}`;
        } catch (error) {
            console.error(error);
            return '$ 0';
        }
    };

    const applyStageFilter = (deals: DataType[]): DataType[] => {
      if(!displayUserSpecificDeals) {
        return deals
      }
      let filterDeals = deals
      if(hideClosed) {
        filterDeals = filterDeals.filter((deal: DataType) => !deal.Stage.includes('Closed'))
      }
      if(hideOpen) {
        filterDeals = filterDeals.filter((deal: DataType) => deal.Stage.includes('Closed'))
      }
      return filterDeals
    };

    const transformDeal = (deal: DealData[]): DataType[] => {
        try {
            const transformedData: DataType[] = deal.map((deal: DealData, index: number) => ({
                key: String(index), // Convert deal_id to string
                dealId: String(deal.deal_id), // Assuming 'dealId' should be the final_version_id
                client: deal.company_name,
                dealName: deal.deal_name,
                startDate: deal.project_start_date ? new Date(deal.project_start_date).toLocaleDateString('en-US'): deal.project_start_date, // Format date
                Stage: deal.deal_stage_name,
                dealAmount: formatCurrency(deal.deal_amount, deal.currency), // Format amount
                dealOwner: deal.deal_owner_name,
                HubSpotLink: deal.hubspot_link,
                canCreatePricing: deal.can_create_pricing,
                duration: deal.duration_in_months,
                currency: deal.currency
            }))
            return transformedData;
        } catch (error) {
            console.error(error);
            return [];
        }
    };

    const createDealPricing = async (dealId: number, dealStartDate?: string | null, duration?: number | null) => {
      if(disableCreateButton) return;

      setDisableCreateButton(true);
      if(!dealStartDate || dealStartDate == null || dealStartDate == '' || !duration || duration == null || duration == 0) {
        message.error("Please enter Start Date and Duration in HubSpot")
        setDisableCreateButton(false)
        return
      }
        const createPricing = await createPricingDeal(dealId);
        if (createPricing.error_status === 401) {
          setTokenExpired(true);
          setDisableCreateButton(false);
        } else {
          if (createPricing.error) {
            console.error('Error Response Data:', createPricing.error);
            console.error('Error Response Status:', createPricing.error_status);
            setDisableCreateButton(false);
            // setCreateDealError(createPricing.error);
          } else {
            setDisableCreateButton(false);
            navigate(`/edit-pricing-details?dealId=${dealId}&versionName=${encodeURIComponent(createPricing.version_name)}`);
          }
        }
      }

    const handleDealRefresh = async (dealId: string) => {
      setRefreshingDeal(dealId)
      await refreshDeals(parseInt(dealId)).then((resp) => {
          if(resp.error ?? '') {
          message.error(resp.error)
          return
          }
          message.success("Successfully Refreshed the Deal");
          setRefreshingDeal('');
      })
      .catch((error) => {
          setRefreshingDeal('');
          message.error("Failed to Refresh the Deal");
      });
    }

    const fetchDealDetails = async () => {
      let deals = await getHubspotDeals();
      if (!Array.isArray(deals)) {
        message.error(deals?.error || 'Failed to load HubSpot deals');
        setDeals([]);
        setFilteredDeals([]);
        setLoading(false);
        return;
      }
      if(displayUserSpecificDeals) {
        deals = deals.filter((deal: DealData) => deal['is_owner'] === true || deal['can_create_pricing'] === true);
      }
      const transformedDeal: any = transformDeal(deals);
      setDeals(transformedDeal);
      setFilteredDeals(transformedDeal)
      setLoading(false);
    }

    useEffect(() => {
        try {
            if (userContext === null) {
                setUserContext(JSON.parse(localStorage.getItem('user_details') as string));
            }
            fetchDealDetails();
        } catch (error) {
            setLoading(false);
            console.error(error);
        }
    }, []);

    useEffect(() => {
      try {
          if (refreshingDeal == '') {
            setLoading(true)
            fetchDealDetails();
          }
      } catch (error) {
          setLoading(false);
          console.error(error);
      }
  }, [refreshingDeal]);

    const generateStageFilters = (data: any[], key: string):any[] => {
      const uniqueValues = Array.from(
          new Set(data.map((item) => {
            try {
                return item[key].toString();
            } catch (error) {
                console.error('Error converting item to string:', { item, key, data });
                return '';
            }
        }))
      ).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
      return uniqueValues.filter((val) => !displayUserSpecificDeals || !(hideClosed || hideOpen ) || ((hideClosed && !val.includes('Closed')) || (hideOpen && val.includes('Closed')))).map((value) => ({ text: value, value }));
  }

    const columns: TableProps<DataType>['columns'] = [
          {
            title: 'Action',
            key: 'action',
            align: 'right',
            render: (text, record) => (
              <div style={{display: 'flex', gap: '4px'}}>
                <Tooltip title="Create Pricing">
                  <Button type="primary" size="small"
                  disabled={!record.canCreatePricing || disableCreateButton}
                    onClick={() => createDealPricing(parseInt(record.dealId),record.startDate, record.duration)}>
                      <FileAddOutlined />
                  </Button>
                </Tooltip>
                <Tooltip title="Refresh Deal Now">
                  <Button size="small" onClick={() => handleDealRefresh(record.dealId)} disabled={refreshingDeal == record.dealId}>
                    <SyncOutlined spin={refreshingDeal == record.dealId}/>
                  </Button>
                </Tooltip>
              </div>
            ),
        },
        {
            title: 'Client',
            dataIndex: 'client',
            key: 'client',
            sorter: (a, b) => a.client.toLowerCase().localeCompare(b.client.toLowerCase()),
            filters: generateTableColumnFilters(deals, 'client'),
            onFilter: (value, record) => record.client === value,
        },
        {
            title: 'Deal Name',
            dataIndex: 'dealName',
            key: 'dealName',
            sorter: (a, b) => a.dealName.toLowerCase().localeCompare(b.dealName.toLowerCase()),
            render: (text, record) => <Button disabled={disableCreateButton || !record.canCreatePricing} type='text' style={{textDecoration: 'underline', color: '#0d6efd'}}
              onClick={() => { if(record.canCreatePricing) createDealPricing(parseInt(record.dealId), record.startDate, record.duration)}}
            >{text}</Button>
        },
        {
            title: 'Start Date',
            dataIndex: 'startDate',
            key: 'startDate',
            sorter: (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
            defaultSortOrder: 'ascend', // Set default sort order
        },
        {
            title: 'Stage',
            dataIndex: 'Stage',
            key: 'Stage',
            sorter: (a, b) => a.Stage.toLowerCase().localeCompare(b.Stage.toLowerCase()),
            filters: generateStageFilters(deals, 'Stage'),
            onFilter: (value, record) => record.Stage === value,
        },
        {
            title: 'Deal Amount',
            dataIndex: 'dealAmount',
            key: 'dealAmount',
            align: 'right',
            sorter: (a, b) => a.dealAmount.localeCompare(b.dealAmount),
        },
        {
            title: 'Deal Owner',
            dataIndex: 'dealOwner',
            key: 'dealOwner',
            sorter: (a, b) => a.dealOwner.toLowerCase().localeCompare(b.dealOwner.toLowerCase()),
            filters: generateTableColumnFilters(deals, 'dealOwner'),
            onFilter: (value, record) => record.dealOwner === value,
        },
        {
          title: 'HubSpot Link',
          dataIndex: 'hubspotLink',
          key: 'hubspotLink',
          align:'center',
          render: (text, record) =>
            <a target="_blank" href={record.HubSpotLink}>{record.dealId}</a>    
        }
    ];

  return (
     <Card 
     title={'Deals with No Pricing'} 
     extra={
        <Search
          placeholder="Search deals"
          value={searchTerm}
          onChange={(e) => handleSearch(e)}
          allowClear
        />
     }>
        <Table
        className='custom-table'
        dataSource={applyStageFilter(filteredDeals)}
        columns={columns}
        scroll={{ x: 'max-content' }}
        loading={loading}
        />
     </Card>
    
  )
}

export default NoPricingDealsTable;
